import type {
	FieldType,
	FormRuleActionTypeValue,
	FormRuleConditionOperatorValue,
	ListenerActionType,
} from "../interfaces/dynamicForm.interface";
import {
	FormRuleActionType,
	FormRuleConditionOperator,
} from "../interfaces/dynamicForm.interface";

// ? Common constants

export const FIELD_TYPES = {
	TEXT: "text",
	NUMBER: "number",
	TELEPHONE: "phone",
	URL: "url",
	CHECKBOX: "checkbox",
	RADIO: "radio",
	SELECT: "select",
	SLIDER: "slider",
	SWITCH: "switch",
	TOGGLE_BUTTON: "toggleButton",
	AUTOCOMPLETE: "autocomplete",
	GROUP: "group",
	FIELD_ARRAY: "fieldArray",
	DATE_PICKER: "date",
	TIME_PICKER: "time",
	DATETIME_PICKER: "dateTime",
	FILE_UPLOAD: "file",
	MULTI_SELECT: "multiSelect",
	USERS: "users",
	SIGNATURE: "signature",
	ADDRESS: "address",
} as const satisfies Record<string, FieldType>;

// Re-export from the interface module for ergonomic imports.
export {
	FormRuleActionType,
	FormRuleConditionLogic,
	FormRuleConditionOperator,
} from "../interfaces/dynamicForm.interface";

export const LISTENER_ACTION_TYPES: Record<string, ListenerActionType> = {
	CLEAR_FIELD: "clear_field",
	SET_FIELD_VALUE: "set_field_value",
	TOGGLE_CHECKBOX: "toggle_checkbox",
	FILTER_OPTIONS: "filter_options",
	ENABLE_FIELD: "enable_field",
	DISABLE_FIELD: "disable_field",
	SHOW_FIELD: "show_field",
	HIDE_FIELD: "hide_field",
	SET_REQUIRED: "set_required",
	SET_OPTIONAL: "set_optional",
};

export const LISTENER_EVENTS = {
	ON_CHANGE: "onChange",
	ON_BLUR: "onBlur",
	ON_MOUNT: "onMount",
	ON_SUBMIT: "onSubmit",
};

// ? Options constants
export const FORM_RULE_ACTION_TYPE_OPTIONS: {
	value: FormRuleActionTypeValue;
	label: string;
}[] = [
	{ value: FormRuleActionType.showField, label: "Show Field" },
	{ value: FormRuleActionType.hideField, label: "Hide Field" },
	{ value: FormRuleActionType.enableField, label: "Enable Field" },
	{ value: FormRuleActionType.disableField, label: "Disable Field" },
	{ value: FormRuleActionType.setRequired, label: "Make Required" },
	{ value: FormRuleActionType.setOptional, label: "Make Optional" },
	{ value: FormRuleActionType.setValidation, label: "Set Validation" },
];

export const FORM_RULE_OPERATOR_OPTIONS: {
	value: FormRuleConditionOperatorValue;
	label: string;
}[] = [
	{ value: FormRuleConditionOperator.equals, label: "Equals" },
	{ value: FormRuleConditionOperator.notEquals, label: "Not Equals" },
	{ value: FormRuleConditionOperator.contains, label: "Contains" },
	{ value: FormRuleConditionOperator.notContains, label: "Not Contains" },
	{ value: FormRuleConditionOperator.greaterThan, label: "Greater Than" },
	{ value: FormRuleConditionOperator.lessThan, label: "Less Than" },
	{
		value: FormRuleConditionOperator.greaterThanOrEqual,
		label: "Greater Than or Equal",
	},
	{
		value: FormRuleConditionOperator.lessThanOrEqual,
		label: "Less Than or Equal",
	},
	{ value: FormRuleConditionOperator.isEmpty, label: "Is Empty" },
	{ value: FormRuleConditionOperator.isNotEmpty, label: "Is Not Empty" },
	{ value: FormRuleConditionOperator.isChecked, label: "Is Checked" },
	{ value: FormRuleConditionOperator.isNotChecked, label: "Is Not Checked" },
	{ value: FormRuleConditionOperator.in, label: "In List" },
	{ value: FormRuleConditionOperator.notIn, label: "Not In List" },
];

// ? Field constants

export const SHOW_PLACEHOLDER_FOR_FIELDS = [
	FIELD_TYPES.TEXT,
	FIELD_TYPES.URL,
	FIELD_TYPES.NUMBER,
	FIELD_TYPES.AUTOCOMPLETE,
	FIELD_TYPES.TELEPHONE,
	FIELD_TYPES.DATE_PICKER,
	FIELD_TYPES.TIME_PICKER,
	FIELD_TYPES.DATETIME_PICKER,
];

// ? Dynamic date/time options for minimum values (past dates)
export const DYNAMIC_MIN_DATE_OPTIONS = [
	{ value: "now", label: "Now" },
	{ value: "yesterday", label: "Yesterday" },
	{ value: "last_week", label: "Last Week" },
	{ value: "one_month_ago", label: "One Month Ago" },
	{ value: "three_months_ago", label: "Three Months Ago" },
	{ value: "six_months_ago", label: "Six Months Ago" },
	{ value: "one_year_ago", label: "One Year Ago" },
	{ value: "start_of_day", label: "Start of Day" },
	{ value: "start_of_week", label: "Start of Week" },
	{ value: "start_of_month", label: "Start of Month" },
	{ value: "start_of_year", label: "Start of Year" },
] as const;

// ? Dynamic date/time options for maximum values (future dates)
export const DYNAMIC_MAX_DATE_OPTIONS = [
	{ value: "now", label: "Now" },
	{ value: "tomorrow", label: "Tomorrow" },
	{ value: "next_week", label: "Next Week" },
	{ value: "one_month_from_now", label: "One Month From Now" },
	{ value: "three_months_from_now", label: "Three Months From Now" },
	{ value: "six_months_from_now", label: "Six Months From Now" },
	{ value: "one_year_from_now", label: "One Year From Now" },
	{ value: "end_of_day", label: "End of Day" },
	{ value: "end_of_week", label: "End of Week" },
	{ value: "end_of_month", label: "End of Month" },
	{ value: "end_of_year", label: "End of Year" },
] as const;

// ? Dynamic time options for minimum values (past times)
export const DYNAMIC_MIN_TIME_OPTIONS = [
	{ value: "now", label: "Now" },
	{ value: "one_hour_ago", label: "1 Hour Ago" },
	{ value: "two_hours_ago", label: "2 Hours Ago" },
	{ value: "three_hours_ago", label: "3 Hours Ago" },
	{ value: "six_hours_ago", label: "6 Hours Ago" },
	{ value: "twelve_hours_ago", label: "12 Hours Ago" },
	{ value: "start_of_day", label: "Start of Day (12 AM)" },
	{ value: "business_start", label: "Business Start (9 AM)" },
	{ value: "lunch_start", label: "Lunch Start (12 PM)" },
] as const;

// ? Dynamic time options for maximum values (future times)
export const DYNAMIC_MAX_TIME_OPTIONS = [
	{ value: "", label: "No maximum" },
	{ value: "1_hour_from_now", label: "1 Hour from Now" },
	{ value: "2_hours_from_now", label: "2 Hours from Now" },
	{ value: "4_hours_from_now", label: "4 Hours from Now" },
	{ value: "8_hours_from_now", label: "8 Hours from Now" },
	{ value: "business_end", label: "Business End (5 PM)" },
	{ value: "end_of_day", label: "End of Day (11:59 PM)" },
] as const;

// Predefined pattern validation options for text fields
export const TEXT_PATTERN_OPTIONS = [
	{ value: "", label: "No pattern" },
	{ value: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$", label: "Email Address" },
	{ value: "^\\+?[1-9]\\d{1,14}$", label: "Phone Number" },
	{
		value: "^\\d{3}-\\d{2}-\\d{4}$",
		label: "Social Security Number (XXX-XX-XXXX)",
	},
	{ value: "^\\d{5}(-\\d{4})?$", label: "ZIP Code (5 or 9 digits)" },
	{ value: "^https?:\\/\\/.+", label: "URL (http/https)" },
	{
		value: "^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$",
		label: "Email",
	},
	{ value: "^\\d{16}$", label: "Credit Card Number (16 digits)" },
	{ value: "^[A-Za-z0-9]{8,}$", label: "Alphanumeric (8+ chars)" },
	{ value: "^[A-Za-z\\s]+$", label: "Letters and spaces only" },
	{ value: "^\\d+$", label: "Numbers only" },
] as const;

export const IGNORE_VALIDATION_AND_LISTENERS_FOR_FIELDS = [
	FIELD_TYPES.GROUP,
	FIELD_TYPES.FIELD_ARRAY,
];

export type DynamicDateOption =
	| (typeof DYNAMIC_MIN_DATE_OPTIONS)[number]["value"]
	| (typeof DYNAMIC_MAX_DATE_OPTIONS)[number]["value"];
export type DynamicTimeOption =
	| (typeof DYNAMIC_MIN_TIME_OPTIONS)[number]["value"]
	| (typeof DYNAMIC_MAX_TIME_OPTIONS)[number]["value"]
	| "one_hour_from_now"
	| "two_hours_from_now"
	| "three_hours_from_now"
	| "six_hours_from_now"
	| "twelve_hours_from_now"
	| "lunch_end";
export type TextPatternOption = (typeof TEXT_PATTERN_OPTIONS)[number]["value"];
